import { useEffect, useMemo, useRef } from 'react';

import {
  Sender,
  MessageType,
} from '@wix/ambassador-innovation-widget-v1-message/types';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import { addMessage, AddMessagePayload } from '@/features';
import { useAppDispatch } from '@/store';
import { useOneChatIntroData } from '@/hooks';

export const useOneChatIntroSequence = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isEditor } = useEnvironment();
  const inFlightRef = useRef(false);
  const {
    readiness,
    intro,
    legal,
    suggestions,
    offline,
    contactForm,
    custom,
    isSequenceFulfilled,
    setIsSequenceFulfilled,
    addCustomMessage,
  } = useOneChatIntroData();

  const payload = useMemo(() => {
    const result: AddMessagePayload[] = [];
    if (intro.shouldSendManualIntro) {
      result.push({
        body: { mainText: intro.manualMessage as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.INTRO,
      });
    }
    if (intro.shouldSendAiIntro) {
      result.push({
        body: { mainText: intro.aiMessage as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.INTRO,
      });
    }
    if (legal.shouldSend) {
      result.push({
        body: { mainText: legal.message as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.LEGAL,
      });
      result.push({
        body: { mainText: t('app.widget.chat.inboxLegalMessage') },
        sender: Sender.SYSTEM,
        messageType: MessageType.SITE_OWNER_NOTIFICATION,
      });
    }
    if (suggestions.shouldSend) {
      const additionalData =
        suggestions.questions?.map(({ text: title }) => ({
          type: 'suggested_question',
          title,
        })) ?? [];
      const mainText = suggestions.introMessage || '';

      result.push({
        body: {
          mainText,
          additionalData,
        },
        sender: Sender.ASSISTANT,
        messageType: MessageType.SUGGESTED_QUESTION,
      });
    }

    if (offline.shouldSend) {
      result.push({
        body: { mainText: offline.message as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.OFFLINE,
      });
    }

    if (contactForm.shouldShowInIntroSequence) {
      result.push({
        body: {
          mainText: contactForm.introMessage ?? t('app.widget.form.beforeText'),
        },
        sender: Sender.ASSISTANT,
        messageType: MessageType.CONTACT_FORM,
      });
    }
    if (custom.shouldSend) {
      result.push({
        body: { mainText: custom.message },
      });
    }
    return result;
  }, [intro, legal, suggestions, offline, contactForm, custom, t]);

  useEffect(() => {
    if (!readiness.loaded || !payload.length || isEditor) {
      return;
    }
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setIsSequenceFulfilled(false);
    dispatch(addMessage(payload)).finally(() => {
      if (custom.shouldSend) {
        addCustomMessage('');
      }
      inFlightRef.current = false;
      setIsSequenceFulfilled(true);
    });
  }, [
    readiness.loaded,
    payload,
    dispatch,
    isEditor,
    custom.shouldSend,
    addCustomMessage,
    setIsSequenceFulfilled,
  ]);

  return {
    isSequenceFulfilled,
  };
};
